
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import "./LearningJourney.css";
import { fetchCompleteResult, getSpeakingCompleteResult, getComprehensionCompleteResult, getLogicCompleteResult } from '../services/api';
import { gradeLevels } from '../data/words';
import { TEST_LIST } from '../data/tests';


type StepStatus = 'done' | 'active' | 'locked' | 'future';
interface Step {
  id: number;
  key: string;
  title: string;
  desc: string;
  icon: string;
  logo: string;
  meta: string;
  status: StepStatus;
  loading: boolean;
  result: any;
}

const baseSteps: Omit<Step, 'status' | 'loading' | 'result'>[] = TEST_LIST.map((test, i) => ({
  id: i + 1,
  key: test.key,
  title: test.title,
  desc: test.desc,
  icon: test.icon,
  logo: test.logo,
  meta: test.meta,
}));


interface LearningJourneyProps {
  childName: string;
  childId: string;
  gradeLevel: number;
  isPaid?: boolean;
  isAdmin?: boolean;
  onStartTest?: (testType: 'spelling' | 'speaking' | 'reading' | 'logic') => void;
  onViewFinal?: (finalData: any) => void;
  onBack?: () => void;
}

const LearningJourney: React.FC<LearningJourneyProps> = ({ childName, childId, gradeLevel, isPaid = false, isAdmin = false, onStartTest, onViewFinal, onBack }) => {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<Step[]>(baseSteps.map(s => ({ ...s, status: 'locked', loading: true, result: null })));
  const [loading, setLoading] = useState(true);
  // Admin-only: when true, every test shows a Start Test button regardless of completion order.
  const [unlockedAll, setUnlockedAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Unpaid children: keep every step locked and skip fetching results.
    if (!isPaid) {
      setSteps(baseSteps.map(s => ({ ...s, status: 'locked', loading: false, result: null })));
      setLoading(false);
      return;
    }
    async function fetchStatuses() {
      setLoading(true);
      const newSteps: Step[] = baseSteps.map(s => ({ ...s, status: 'locked', loading: true, result: null }));
      const statuses = await Promise.all([
        // Spelling
        fetchCompleteResult(childId, gradeLevels[gradeLevel]?.name).catch(() => null),
        // Speaking
        getSpeakingCompleteResult(childId, gradeLevels[gradeLevel]?.name).catch(() => null),
        // Reading (Comprehension)
        getComprehensionCompleteResult(childId, gradeLevels[gradeLevel]?.name).catch(() => null),
        // Logic (test_id omitted: backend returns the latest logic result for the child)
        getLogicCompleteResult(childId, undefined, gradeLevels[gradeLevel]?.name).catch(() => null),
      ]);
      let firstActive = -1;
      for (let i = 0; i < newSteps.length; ++i) {
        const hasResult = statuses[i] && ((Array.isArray(statuses[i]) && statuses[i].length > 0) || (typeof statuses[i] === 'object' && Object.keys(statuses[i]).length > 0));
        newSteps[i].status = hasResult ? 'done' : 'locked';
        newSteps[i].loading = false;
        newSteps[i].result = statuses[i];
        if (!hasResult && firstActive === -1) firstActive = i;
      }
      if (firstActive !== -1) newSteps[firstActive].status = 'active';
      if (!cancelled) {
        setSteps(newSteps);
        setLoading(false);
      }
    }
    fetchStatuses();
    return () => { cancelled = true; };
  }, [childId, gradeLevel, isPaid]);

  const handlePrompt = (message: string) => {
    // Replace with your application action (e.g., navigation, modal, etc.)
    // For now, just log
    console.log(message);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/app');
    }
  };

  const activeIndex = steps.findIndex((s) => s.status === 'active');
  // If all done, progress is 100%
  const allDone = steps.every(s => s.status === 'done');
  const progressPct = allDone ? 100 : Math.round((Math.max(activeIndex, 0) / (steps.length - 1)) * 100);

  const goToPayment = () => {
    navigate('/app/payment', { state: { preselectChildId: childId } });
  };

  return (
    <div className="scene">
      {/* Sticky Header */}
      <div className="sticky-header">
        <div className="header-inner">
          <div className="header-card">
            <div className="header-top">
              <div className="hrow">
                <button className="back-btn" onClick={handleBack} aria-label="Back to Dashboard" title="Back to Dashboard">
                  <span aria-hidden="true">←</span>
                  <span className="back-btn-label">Dashboard</span>
                </button>
                <div className="avatar-wrap">🧒</div>
                <div className="title-block">
                  <div className="main-title">{childName}&apos;s Assessment</div>
                  <div className="sub-title">
                    Complete these tests to identify areas for improvement.
                  </div>
                </div>
              </div>
              <div className="step-chip">
                <div className="pip"></div>
                {!isPaid ? '🔒 Locked' : `Step ${Math.max(activeIndex, 0) + 1} of ${steps.length}`}
                <div className="pip"></div>
              </div>
              {isAdmin && isPaid && (
                <div className="admin-unlock-row">
                  <button
                    className={`admin-unlock-btn${unlockedAll ? ' active' : ''}`}
                    onClick={() => setUnlockedAll((v) => !v)}
                    title={unlockedAll ? 'Re-lock tests in order' : 'Admin: show Start Test on every test'}
                  >
                    {unlockedAll ? '🔒 Re-lock Tests' : '🔓 Unlock All Tests'}
                  </button>
                </div>
              )}
            </div>

            <div className="prog-track">
              <div className="prog-fill" style={{ width: `${progressPct}%` }}></div>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        {/* Payment required banner */}
        {!isPaid && (
          <div
            style={{
              margin: '0 auto 20px',
              maxWidth: 640,
              background: 'linear-gradient(90deg, #FFF7EE, #FFF0F6)',
              border: '2px solid rgba(247,178,106,0.45)',
              borderRadius: 20,
              padding: '18px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <span style={{ fontSize: 26 }}>🔒</span>
              <div>
                <div style={{ fontWeight: 700, color: '#1F224B' }}>
                  Unlock {childName}&apos;s assessments
                </div>
                <div style={{ fontSize: 13, color: '#6B6FA3' }}>
                  A one-time payment unlocks all four activities and the full learning report.
                </div>
              </div>
            </div>
            <button
              onClick={goToPayment}
              style={{
                background: 'linear-gradient(90deg, #F7B26A, #F76C8A)',
                color: '#fff',
                fontWeight: 700,
                border: 'none',
                borderRadius: 999,
                padding: '12px 20px',
                cursor: 'pointer',
                boxShadow: '0 6px 16px rgba(247,108,138,0.35)',
              }}
            >
              Unlock now
            </button>
          </div>
        )}
        {/* Timeline */}
        <div className="track-wrap">
          <div className="center-spine">
            <div className="spine-progress" style={{ height: `${progressPct}%` }}></div>
          </div>
          {steps.map((step, idx) => {
            const isReverse = idx % 2 === 1;
            let cardClass = 'card';
            if (step.status === 'done') cardClass += ' done';
            if (step.status === 'active') cardClass += ' active';
            if (step.status === 'locked') cardClass += ' locked';
            if (step.status === 'future') cardClass += ' future';
            let nodeClass = 'node';
            if (step.status === 'done') nodeClass += ' done';
            if (step.status === 'active') nodeClass += ' active';
            if (step.status === 'locked') nodeClass += ' locked';
            if (step.status === 'future') nodeClass += ' future';
            let nodeColClass = 'node-col';
            if (step.status === 'done') nodeColClass += ' done-col';
            if (step.status === 'active') nodeColClass += ' active-col';
            if (step.status === 'locked' || step.status === 'future') nodeColClass += ' pending-col';
            return (
              <div className={`row${isReverse ? ' reverse' : ''}`} key={step.id}>
                <div className={cardClass}>
                  <div className="c-head">
                    <div className="c-icon">
                      <img
                        src={step.logo}
                        alt={step.title}
                        className="c-logo"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.style.display = 'inline';
                        }}
                      />
                      <span style={{ display: 'none' }}>{step.icon}</span>
                    </div>
                    <div className="c-head-text">
                      <div className="c-step">
                        STEP {String(step.id).padStart(2, '0')}{step.status === 'active' ? ' · NOW' : ''}
                      </div>
                      <div className="c-name">{step.title}</div>
                      <div className="c-desc">{step.desc}</div>
                    </div>
                  </div>
                  {step.loading ? (
                    <div className="badge">Loading...</div>
                  ) : step.status === 'done' ? (
                    <div className="badge">✓ Completed</div>
                  ) : (step.status === 'locked' || step.status === 'future') ? (
                    unlockedAll ? (
                      <button
                        className="c-btn"
                        onClick={() => {
                          if (onStartTest) {
                            if (step.key === 'spelling' || step.key === 'speaking' || step.key === 'reading' || step.key === 'logic') {
                              onStartTest(step.key as 'spelling' | 'speaking' | 'reading' | 'logic');
                            }
                          } else {
                            handlePrompt(`Continue ${step.title} for ${childName}`);
                          }
                        }}
                      >
                        ▶ Start Test
                      </button>
                    ) : null
                  ) : step.status === 'active' ? (
                    <button
                      className="c-btn"
                      onClick={() => {
                        if (onStartTest) {
                          if (step.key === 'spelling' || step.key === 'speaking' || step.key === 'reading' || step.key === 'logic') {
                            onStartTest(step.key as 'spelling' | 'speaking' | 'reading' | 'logic');
                          }
                        } else {
                          handlePrompt(`Continue ${step.title} for ${childName}`);
                        }
                      }}
                    >
                      ▶ Start Test
                    </button>
                  ) : null}
                  <div className={`c-meta${step.status === 'active' ? ' active-text' : ''}`}>{step.meta}</div>
                </div>
                <div className={nodeColClass}>
                  <div className="n-line-t"></div>
                  <div className={nodeClass}>{step.id}</div>
                  <div className="n-line-b"></div>
                </div>
                <div className="empty-space"></div>
              </div>
            );
          })}
        </div>
        {/* CTA */}
        <div className="cta-box">
          <div className="cta-title">
            {!isPaid ? 'Unlock to begin' : allDone ? 'All assessments completed!' : 'Continue your assessment'}
          </div>
          <div className="cta-sub">
            {!isPaid
              ? `Unlock ${childName}'s profile with a one-time payment to start the assessments.`
              : allDone
              ? `${childName} has completed all tests. View the final result below.`
              : `Start the next test to identify ${childName}'s learning needs.`}
          </div>
          {!isPaid ? (
            <button className="cta-btn" onClick={goToPayment}>
              🔓 Unlock Assessments
            </button>
          ) : allDone ? (
            <button
              className="cta-btn"
              onClick={() => {
                // Assemble combined final data from each step's result
                const combined: any = { data: {}, parent_summary: {} };
                try {
                  const strengthsArr: string[] = [];
                  const areasArr: string[] = [];
                  let recommendation = '';
                  steps.forEach((s) => {
                    combined.data[s.key] = s.result;
                    const ps = s.result && (s.result.parent_summary || (Array.isArray(s.result) ? (s.result[0] && s.result[0].parent_summary) : null));
                    if (ps) {
                      if (Array.isArray(ps.strengths)) strengthsArr.push(...ps.strengths);
                      if (Array.isArray(ps.areas_to_develop)) areasArr.push(...ps.areas_to_develop);
                      if (!recommendation && (ps.recommendation || ps.suggestion)) recommendation = ps.recommendation || ps.suggestion;
                    }
                  });
                  combined.parent_summary = {
                    strengths: Array.from(new Set(strengthsArr)).slice(0, 6),
                    areas_to_develop: Array.from(new Set(areasArr)).slice(0, 6),
                    recommendation: recommendation
                  };
                } catch (e) {
                  console.error('Error building combined final data', e);
                }
                if (onViewFinal) {
                  onViewFinal(combined);
                } else {
                  console.log('Final data ready', combined);
                }
              }}
            >
              🏁 View Final Result
            </button>
          ) : (
            <button
              className="cta-btn"
              onClick={() => {
                const activeStep = steps[activeIndex];
                if (activeStep && onStartTest) {
                  if (activeStep.key === 'spelling' || activeStep.key === 'speaking' || activeStep.key === 'reading' || activeStep.key === 'logic') {
                    onStartTest(activeStep.key as 'spelling' | 'speaking' | 'reading' | 'logic');
                  }
                }
              }}
              disabled={loading || activeIndex === -1}
            >
              📝 Start Next Test
            </button>
          )}
          <div className="cta-time">
            {allDone ? '🎉 Congratulations!' : 'Takes less than 10 minutes to complete'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningJourney;